import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { EmptyList } from '../../../../shared/empty-list/EmptyList';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { AddButton } from '../../../../shared/button/AddButton';
import TransparentButton from '../../../../shared/button/transparent-button/TransparentButton';
import { PageActionsRow } from '../../../../shared/headers/PageActionsRow';
import FriendlyDate from '../../../../shared/FriendlyDate';
import { TimeZoneNote } from '../../../../shared/time-zone-note/TimeZoneNote';
import { Pager } from '../../../../shared/pager/Pager';
import { Sorter } from '../../../../shared/table/Sorter';
import '../../../../shared/table/AlertsTable.scss';
import { ConfirmModal } from '../common/ConfirmModal';
import { RunStatusBadge } from '../common/RunStatusBadge';
import { RunProgress } from '../common/RunProgress';
import { ConnectionHint } from '../common/ConnectionHint';
import { LimitsBanner } from './LimitsBanner';
import {
    cancelRun,
    deleteRun,
    fetchRuns,
    fetchScenarios,
    fetchSimulationLimits,
    followRunsList,
    stopFollowingRunsList
} from '../redux/runs/actions';
import {
    getLimitsState,
    getRunsListState,
    getScenariosState
} from '../redux/selectors';
import { redirectTo } from '../../../../routing/redirectService';
import {
    getRunRole,
    getScenarioName,
    isActiveStatus,
    isTerminalStatus,
    RunRole
} from '../simulation.model';
import { SimulationPath } from '../simulation.paths';

/* sortKey: what the API sorts by, for the columns that it can sort by */
const runHeaders = [
    { key: 'RUN', value: 'Run', sortKey: 'ID' },
    { key: 'SANDBOX', value: 'Sandbox' },
    { key: 'STATUS', value: 'Status', sortKey: 'STATUS' },
    { key: 'SCENARIOS', value: 'Scenarios' },
    { key: 'PROGRESS', value: 'Progress' },
    { key: 'ALERTS', value: 'Alerts' },
    { key: 'CREATED', value: 'Created', sortKey: 'CREATED_AT' },
    { key: 'ACTIONS', value: '' }
];

const RunAction = Object.freeze({ CANCEL: 'CANCEL', DELETE: 'DELETE' });

class RunsListContainer extends Component {
    constructor(props) {
        super(props);
        this.state = { pending: null };
        this.confirmRef = React.createRef();
    }

    componentDidMount() {
        this.props.fetchSimulationLimits();
        // The names of the scenarios are the catalog's; a run only has their ids
        this.props.fetchScenarios();
        this.fetchFromState();
        this.props.followRunsList();
    }

    componentWillUnmount() {
        this.props.stopFollowingRunsList();
    }

    fetchFromState = () => {
        const { pagination, sort } = this.props;

        this.props.fetchRuns({
            page: pagination.page,
            pageSize: pagination.pageSize,
            ...sort
        });
    };

    tryAgain = () => {
        this.fetchFromState();
        this.props.followRunsList();
    };

    updatePagination = ({ page, pageSize }) =>
        this.props.fetchRuns({ page, pageSize, ...this.props.sort });

    updateSort = ({ sortingKey, sortDirection }) => {
        const header = _.find(runHeaders, h => h.key === sortingKey);

        this.props.fetchRuns({
            page: 1,
            pageSize: this.props.pagination.pageSize,
            sortType: header && header.sortKey,
            sortOrder: sortDirection
        });
    };

    ask = (action, run) =>
        this.setState({ pending: { action, run } }, () =>
            this.confirmRef.current.open()
        );

    confirmPending = () => {
        const { pending } = this.state;

        if (pending && pending.action === RunAction.CANCEL) {
            this.props.cancelRun({ runId: pending.run.id });
        } else if (pending && pending.action === RunAction.DELETE) {
            this.props.deleteRun({ runId: pending.run.id });
        }
    };

    renderRole = run => {
        const role = getRunRole(run);

        return role === RunRole.VARIANT ? (
            <div>
                <span className={'sim-tag variant'}>
                    variant of #{run.baselineRunId}
                </span>
            </div>
        ) : role === RunRole.BASELINE ? (
            <div>
                <span className={'sim-tag'}>baseline</span>
            </div>
        ) : null;
    };

    renderRow = run => {
        const busy = !!this.props.busy[run.id];
        const scenarios = run.scenarios || [];

        return (
            <tr key={run.id} className="data">
                <td>
                    <div>
                        <strong>#{run.id}</strong>
                    </div>
                    {run.label && (
                        <div className={'sim-muted'}>{run.label}</div>
                    )}
                    {this.renderRole(run)}
                </td>
                <td>{run.workspace}</td>
                <td>
                    <RunStatusBadge status={run.status} />
                </td>
                <td>
                    {scenarios.slice(0, 2).map(id => (
                        <div key={id}>
                            {getScenarioName(this.props.scenariosById, id)}
                        </div>
                    ))}
                    {scenarios.length > 2 && (
                        <div className={'sim-muted'}>
                            and {scenarios.length - 2} more
                        </div>
                    )}
                </td>
                <td style={{ minWidth: 150 }}>
                    <RunProgress progress={run.progress} status={run.status} />
                </td>
                <td>{_.get(run, 'alerts.total', 0)}</td>
                <td>
                    <FriendlyDate timestamp={run.createdAt} />
                    <div className={'sim-muted'}>
                        by {_.get(run, 'createdBy.name')}
                    </div>
                </td>
                <td className={'sim-actions'}>
                    <TransparentButton
                        onClick={() => redirectTo(SimulationPath.run(run.id))}
                    >
                        Open
                    </TransparentButton>
                    {isActiveStatus(run.status) && (
                        <TransparentButton
                            disabled={busy}
                            onClick={() => this.ask(RunAction.CANCEL, run)}
                        >
                            Cancel
                        </TransparentButton>
                    )}
                    {isTerminalStatus(run.status) && (
                        <TransparentButton
                            disabled={busy}
                            onClick={() => this.ask(RunAction.DELETE, run)}
                        >
                            Delete
                        </TransparentButton>
                    )}
                </td>
            </tr>
        );
    };

    describePending = () => {
        const { pending } = this.state;

        if (!pending) {
            return {};
        }
        const id = pending.run.id;

        return pending.action === RunAction.CANCEL
            ? {
                  title: `Cancel run #${id}`,
                  confirmText: 'Cancel run',
                  message:
                      'The run is stopped. What it produced so far is kept.'
              }
            : {
                  title: `Delete run #${id}`,
                  confirmText: 'Delete run',
                  message:
                      'The run and everything it produced are deleted. This cannot be undone.'
              };
    };

    render() {
        const {
            runs,
            loaded,
            loading,
            pagination,
            sort,
            limits,
            connection
        } = this.props;
        const description = this.describePending();

        return (
            <div className={'sim-runs'}>
                <ConnectionHint
                    connection={connection}
                    onRetry={this.tryAgain}
                />
                <LimitsBanner limits={limits} />
                <PageActionsRow>
                    <AddButton
                        key={'new-run'}
                        onClick={() => redirectTo(SimulationPath.NEW_RUN)}
                        label={'New run'}
                    />
                </PageActionsRow>

                <div className={'table'}>
                    <TimeZoneNote />
                    <table>
                        <tbody>
                            <tr className="headers">
                                {runHeaders.map(header => (
                                    <th key={header.key}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>{header.value}</span>
                                            {header.sortKey && (
                                                <Sorter
                                                    sortingKey={header.key}
                                                    currentSortingKey={
                                                        (
                                                            _.find(runHeaders, {
                                                                sortKey:
                                                                    sort.sortType
                                                            }) || {}
                                                        ).key
                                                    }
                                                    onSort={this.updateSort}
                                                />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                            {loading && !loaded ? (
                                <tr>
                                    <td colSpan={runHeaders.length}>
                                        <SpinLoader />
                                    </td>
                                </tr>
                            ) : _.isEmpty(runs) ? (
                                <tr className="data">
                                    <td
                                        colSpan={runHeaders.length}
                                        style={{ textAlign: 'center' }}
                                    >
                                        <EmptyList />
                                    </td>
                                </tr>
                            ) : (
                                runs.map(this.renderRow)
                            )}
                            <Pager
                                pagination={pagination}
                                onPaginationChanged={this.updatePagination}
                                colspan={runHeaders.length}
                            />
                        </tbody>
                    </table>
                </div>

                <ConfirmModal
                    ref={this.confirmRef}
                    title={description.title}
                    message={description.message}
                    confirmText={description.confirmText}
                    onConfirm={this.confirmPending}
                />
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        data,
        loaded,
        loading,
        pagination,
        sort,
        busy,
        connection
    } = getRunsListState(state);

    return {
        runs: data,
        loaded,
        loading,
        pagination,
        sort,
        busy,
        connection,
        scenariosById: _.keyBy(getScenariosState(state).data, 'id'),
        limits: getLimitsState(state).data
    };
};

const mapDispatchToProps = dispatch => ({
    fetchRuns: args => dispatch(fetchRuns(args)),
    fetchScenarios: () => dispatch(fetchScenarios()),
    followRunsList: () => dispatch(followRunsList()),
    stopFollowingRunsList: () => dispatch(stopFollowingRunsList()),
    fetchSimulationLimits: () => dispatch(fetchSimulationLimits()),
    cancelRun: args => dispatch(cancelRun(args)),
    deleteRun: args => dispatch(deleteRun(args))
});

export default connect(mapStateToProps, mapDispatchToProps)(RunsListContainer);
